import { RefObject } from 'react';
import ReactDOMServer from 'react-dom/server';
import { EnergyTrendsTooltipGraph } from '../components/GraphTooltip';
import {
  BarSeriesOption, dispose, EChartsOption, init,
  SeriesOption,
} from 'echarts';
import { getArrayDaysOfMonth } from 'helpers/getArrayDaysOfMonth';
import moment from 'moment-timezone';
import { createYAxis } from '../helpers';
import { colors } from '~/styles/colors';
import { LineSeriesOption } from 'echarts/charts';
import { generateDisabledData } from '../components/constants';
import { EnergyTrendsData } from '../types';
import { FONT_FAMILY } from '~/styles/fonts';

interface UseGenerateGraphReturn {
  renderEnergyTrendsGraph: (data: EnergyTrendsData | undefined) => void;
}

interface TooltipParams {
  name: string;
  value: number;
  dataIndex: number;
}

interface TrendsDataFormatted {
  consumption: NonNullable<BarSeriesOption['data']>;
  consumptionForecastGraphic: NonNullable<BarSeriesOption['data']>;
  consumptionOverTarget: NonNullable<BarSeriesOption['data']>;
  consumptionTotal: NonNullable<BarSeriesOption['data']>;
  consumptionTarget: NonNullable<LineSeriesOption['data']>;
}

export function useGenerateEnergyTrendsGraph(
  containerRef: RefObject<HTMLDivElement>,
  disabled?: boolean,
): UseGenerateGraphReturn {
  const currentMonth = moment().format('MMMM');
  const currentYear = moment().format('YYYY');

  function createChartOptions(data: EnergyTrendsData): EChartsOption {
    const option: EChartsOption = {
      tooltip: {
        show: !disabled,
        trigger: 'axis',
        formatter: (value) => formatterTooltip(value, data.trendsData),
        axisPointer: {
          type: 'shadow',
          axis: 'x',
        },
        shadowBlur: 2,
        shadowOffsetX: 1,
        shadowOffsetY: 0,
        confine: true,
      },
      grid: {
        left: '3%',
        right: '5%',
        bottom: '2%',
        top: '14px',
        width: '92%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: createXAxis(),
        boundaryGap: false,
        splitLine: {
          show: true,
          interval: 0,
          lineStyle: {
            type: 'dashed',
          },
        },
        axisLabel: {
          show: true,
          align: 'center',
          margin: 8,
          showMaxLabel: true,
          interval: (index: number) => {
            if (index === 0) return true;

            return (index + 1) % 5 === 0;
          },
          color: colors.text['main-900'],
          fontFamily: FONT_FAMILY,
          fontSize: 9,
          lineHeight: 12,
          fontWeight: 400,
        },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      yAxis: createYAxis(),
      series: formatterSeries(data),
      media: [
        {
          query: { maxWidth: 768 },
          option: {
            series: [
              { barWidth: '8px' },
            ],
            yAxis: {
              offset: 4,
            },
          },
        },
      ],
    };

    return option;
  }

  function createXAxis(): number[] {
    return getArrayDaysOfMonth(
      Number(moment().month(currentMonth).format('M')),
      Number(currentYear),
    );
  }

  function formatTrendsData(data: EnergyTrendsData): TrendsDataFormatted {
    const trendsFormatted: TrendsDataFormatted = {
      consumption: [],
      consumptionForecastGraphic: [],
      consumptionOverTarget: [],
      consumptionTarget: [],
      consumptionTotal: [],
    };

    const actualDate = moment().startOf('day');

    for (const trend of data.trendsData) {
      const isOverTarget = trend.consumptionOverTarget > 0;
      const showForecastBar = trend.consumption === 0;
      const trendConsumption = trend.consumption;
      const isBeforeActualDate = moment(trend.time).startOf('day').isBefore(actualDate);

      if (isOverTarget) {
        trendsFormatted.consumption.push({
          value: trendConsumption - Number(trend.consumptionOverTarget),
          itemStyle: {
            borderRadius: [0, 0, 0, 0],
          },
        });
      }
      else {
        trendsFormatted.consumption.push({
          value: trendConsumption,
          itemStyle: {
            borderRadius: [2, 2, 0, 0],
          },
        });
      }

      trendsFormatted.consumptionOverTarget.push(Number(trend.consumptionOverTarget));

      if (showForecastBar && !isBeforeActualDate) {
        trendsFormatted.consumptionForecastGraphic.push(Number(trend.consumptionForecast) - trendConsumption);
      } else {
        trendsFormatted.consumptionForecastGraphic.push(0);
      }

      trendsFormatted.consumptionTarget.push(Number(trend.consumptionTarget));
    }

    return trendsFormatted;
  }

  function formatterSeries(data: EnergyTrendsData): SeriesOption[] {
    const trendsFormatted = formatTrendsData(data);

    const series: SeriesOption[] = [
      {
        data: trendsFormatted.consumption,
        type: 'bar',
        stack: 'consumption',
        name: 'consumption',
        barWidth: '10px',
        emphasis: {
          focus: 'none',
        },
        color: disabled ? colors.icon['disabled-300'] : colors.state.success.base,
      },
      {
        data: trendsFormatted.consumptionOverTarget,
        type: 'bar',
        stack: 'consumption',
        stackStrategy: 'all',
        name: 'overConsumption',
        itemStyle: {
          color: colors.state.danger.base,
          borderRadius: [2, 2, 0, 0],
        },
        barWidth: '10px',
        emphasis: {
          focus: 'none',
        },
      },
      {
        data: trendsFormatted.consumptionForecastGraphic,
        type: 'bar',
        stack: 'consumption',
        stackStrategy: 'all',
        name: 'forecastConsumption',
        itemStyle: {
          color: colors.icon['disabled-300'],
          borderRadius: [2, 2, 0, 0],
        },
        barWidth: '10px',
        emphasis: {
          focus: 'none',
        },
      },
    ];

    if (data.monthlyTarget) {
      series.push({
        name: 'targetConsumption',
        type: 'line',
        data: trendsFormatted.consumptionTarget,
        itemStyle: {
          opacity: 0,
        },
        lineStyle: {
          width: 1.5,
          type: 'dashed',
          color: colors.bg['primary-base'],
        },
      });
    }

    return series;
  }

  function formatterTooltip([value]: TooltipParams[], data: unknown): string {
    return ReactDOMServer.renderToString(
      <EnergyTrendsTooltipGraph
        index={value.dataIndex}
        trendsData={data as EnergyTrendsData['trendsData']}
      />,
    );
  }

  function renderEnergyTrendsGraph(data: EnergyTrendsData | undefined): void | (() => void) {
    if (!containerRef.current) return;

    const newData = data ?? generateDisabledData();

    dispose(containerRef.current);
    const chartInstance = init(containerRef.current);

    const resizeHandler = () => {
      chartInstance.resize();
    };

    window.addEventListener('resize', resizeHandler);

    chartInstance.setOption(createChartOptions(newData));

    return (): void => {
      window.removeEventListener('resize', resizeHandler);
      chartInstance.dispose();
    };
  }

  return {
    renderEnergyTrendsGraph,
  };
}
